// NOMBASE — Cmd-K command bar (Operations v2 Phase 4).
// Universal entry point. Press ⌘K (or Ctrl+K) anywhere to open.
//
// 12 typed tools, regex-routed today (LLM-assisted parsing comes later).
// Each tool resolves to either a navigation, a server call, or both.
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Command, ArrowRight, Calendar, Send, Plus, Search, Move, Package, Building2, FlaskConical, Boxes, Factory, Tag, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';

// Map kind → icon so the unified search results have a visual hint.
const KIND_ICON = {
  product: Package,
  supplier: Building2,
  manufacturer: Factory,
  formula: FlaskConical,
  ingredient: Boxes,
  packaging: Package,
  variant: Tag,
  po: ShoppingBag,
  sku: Tag,
  hit: ArrowRight,
};

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({});

// ─── Tool registry — 12 typed actions ──────────────────────────────────────
// Each tool: { id, label, hint, match(input) → action OR null }
// `action` is { run() }.  Returning null means the tool didn't match.
const buildTools = (navigate) => [
  {
    id: 'jump_today',
    label: 'Jump to today',
    hint: 'today',
    icon: Calendar,
    match: (q) => /^today$/i.test(q.trim()) ? {
      run: () => { navigate('/plan'); toast('Jumped to today.'); },
    } : null,
  },
  {
    id: 'jump_to_date',
    label: 'Jump to date',
    hint: 'jun 7',
    icon: Calendar,
    match: (q) => {
      const d = parseDate(q.trim());
      if (!d) return null;
      return {
        label: `Jump to ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`,
        run: () => {
          navigate('/plan');
          toast(`Jumped to ${d.toLocaleDateString()}.`);
        },
      };
    },
  },
  {
    id: 'open_buy',
    label: 'Open Buy',
    hint: 'buy',
    icon: Send,
    match: (q) => /^(buy|orders|decisions)$/i.test(q.trim()) ? {
      run: () => navigate('/buy'),
    } : null,
  },
  {
    id: 'open_plan',
    label: 'Open Plan',
    hint: 'plan',
    icon: Calendar,
    match: (q) => /^(plan|calendar|production)$/i.test(q.trim()) ? {
      run: () => navigate('/plan'),
    } : null,
  },
  {
    id: 'open_receive',
    label: 'Open Receive',
    hint: 'receive',
    icon: Plus,
    match: (q) => /^(receive|receiving|incoming)$/i.test(q.trim()) ? {
      run: () => navigate('/receive'),
    } : null,
  },
  {
    id: 'new_production_run',
    label: 'New production run',
    hint: 'new run',
    icon: Plus,
    match: (q) => /^(new\s+(run|production)|make|produce|schedule\s+(run|production))$/i.test(q.trim()) ? {
      run: () => navigate('/production/create'),
    } : null,
  },
  {
    id: 'send_all',
    label: 'Send all buy decisions',
    hint: 'send all',
    icon: Send,
    match: (q) => /^send\s+all/i.test(q.trim()) ? {
      run: async () => {
        try {
          const r = await fetch(`${API}/api/buy/decisions`, { credentials: 'include', headers: hdrs() });
          const data = await r.json();
          const decisions = data?.decisions || [];
          if (!decisions.length) {
            toast('Nothing to send.');
            return;
          }
          let sent = 0;
          for (const d of decisions) {
            const res = await fetch(`${API}/api/buy/decisions/send`, { credentials: 'include',
              method: 'POST',
              headers: { ...hdrs(), 'Content-Type': 'application/json' },
              body: JSON.stringify({
                supplier_id: d.supplier_id, supplier_name: d.supplier_name,
                line_items: d.line_items, notes: 'Sent via Cmd-K',
              }),
            });
            if (res.ok) sent++;
          }
          toast.success(`Sent ${sent} of ${decisions.length} POs.`);
          navigate('/buy');
        } catch {
          toast.error('Send-all failed.');
        }
      },
    } : null,
  },
  {
    id: 'add_demand',
    label: 'Add demand',
    hint: 'add demand 5000 of <sku> on jun 7',
    icon: Plus,
    match: (q) => {
      const m = q.trim().match(/^add\s+demand\s+(\d+(?:\.\d+)?)\s+(?:of\s+)?(.+?)\s+(?:on|by|for)\s+(.+)$/i);
      if (!m) return null;
      const [, qty, skuName, when] = m;
      const date = parseDate(when);
      if (!date) return null;
      return {
        label: `Add demand: ${qty} of "${skuName}" on ${date.toLocaleDateString()}`,
        run: async () => {
          // Resolve SKU by name via /find
          const res = await fetch(`${API}/api/plan/find?q=${encodeURIComponent(skuName)}`, { headers: hdrs() });
          const hits = await res.json();
          const sku = hits.find((h) => h.kind === 'sku');
          if (!sku) { toast.error(`No SKU matching "${skuName}".`); return; }
          const r = await fetch(`${API}/api/plan/demand`, { credentials: 'include',
            method: 'POST',
            headers: { ...hdrs(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sku_id: sku.id, quantity: parseFloat(qty),
              target_date: date.toISOString(), confidence: 100, source: 'internal',
            }),
          });
          if (r.ok) { toast.success('Demand added.'); navigate('/plan'); }
          else toast.error('Could not add demand.');
        },
      };
    },
  },
  {
    id: 'find',
    label: 'Find anything',
    hint: 'find <query>',
    icon: Search,
    match: (q) => {
      const m = q.trim().match(/^find\s+(.+)$/i);
      if (!m) return null;
      const needle = m[1].trim();
      return {
        label: `Find "${needle}"`,
        run: async () => {
          const res = await fetch(
            `${API}/api/search?q=${encodeURIComponent(needle)}&limit=5`,
            { headers: hdrs() },
          );
          const data = await res.json();
          const flat = [];
          Object.values(data?.groups || {}).forEach((hits) => hits.forEach((h) => flat.push(h)));
          if (flat.length === 0) { toast(`Nothing found for "${needle}".`); return; }
          const top = flat[0];
          toast.success(`${top.label} → ${top.sublabel}`);
          if (top.route) navigate(top.route);
        },
      };
    },
  },
  // Future tools (typed registry stays explicit — fill in next session):
  //   move_run, cancel_run, cheapest_supplier, what_if_demand, explain_run
];

// ─── Loose date parser for "today | tomorrow | jun 7 | 6/14 | 2026-06-14" ──
function parseDate(input) {
  if (!input) return null;
  const s = input.trim().toLowerCase();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (s === 'today') return today;
  if (s === 'tomorrow') {
    const d = new Date(today); d.setDate(d.getDate() + 1); return d;
  }
  // ISO yyyy-mm-dd
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
  // Numeric m/d or m/d/yy
  m = s.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/);
  if (m) {
    const yy = m[3] ? parseInt(m[3]) : today.getFullYear();
    const year = yy < 100 ? 2000 + yy : yy;
    return new Date(year, parseInt(m[1]) - 1, parseInt(m[2]));
  }
  // Month name + day
  const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  m = s.match(/^([a-z]{3,})\s+(\d{1,2})(?:,?\s+(\d{4}))?$/);
  if (m) {
    const monIdx = months.indexOf(m[1].slice(0, 3));
    if (monIdx >= 0) {
      const y = m[3] ? parseInt(m[3]) : today.getFullYear();
      return new Date(y, monIdx, parseInt(m[2]));
    }
  }
  return null;
}

// ─── The component ─────────────────────────────────────────────────────────
export default function CmdK() {
  const navigate = useNavigate();
  const tools = useMemo(() => buildTools(navigate), [navigate]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [findHits, setFindHits] = useState([]);
  const inputRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false); setQuery(''); setActiveIdx(0); setFindHits([]);
  }, []);

  // Global ⌘K / Ctrl+K opens the bar.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault(); setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  // Focus input when opened.
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);

  // Live `find` results when there's no other matching tool.
  // Hits the unified `/api/search` endpoint (replaces the old plan-only
  // `/api/plan/find`). Results across products, suppliers, formulas,
  // ingredients, packaging, manufacturers, variants, and POs.
  useEffect(() => {
    if (!query.trim()) { setFindHits([]); return; }
    const q = query.trim();
    const explicitMatch = tools.some((t) => t.match(q));
    if (explicitMatch) { setFindHits([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API}/api/search?q=${encodeURIComponent(q)}&limit=3`,
          { headers: hdrs() },
        );
        if (!res.ok) {
          // Fallback to legacy /api/plan/find for brand users on older
          // sessions until everyone's migrated.
          const legacy = await fetch(
            `${API}/api/plan/find?q=${encodeURIComponent(q)}`,
            { headers: hdrs() },
          );
          const legacyHits = legacy.ok ? await legacy.json() : [];
          if (!cancelled) setFindHits(legacyHits || []);
          return;
        }
        const data = await res.json();
        // Flatten grouped results into the same shape the renderer expects:
        // { kind, id, label, sublabel, route }
        const flat = [];
        Object.entries(data?.groups || {}).forEach(([, hits]) => {
          hits.forEach((h) => flat.push(h));
        });
        if (!cancelled) setFindHits(flat);
      } catch { if (!cancelled) setFindHits([]); }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, tools]);

  const matchedTools = useMemo(
    () => tools.map((t) => ({ tool: t, action: query ? t.match(query) : null })).filter((x) => x.action),
    [query, tools],
  );

  // All visible items in order: matched tools, then find hits.
  // Tools use their own icon; find hits map kind → icon + a kind chip.
  const items = useMemo(() => [
    ...matchedTools.map(({ tool, action }) => ({
      kind: 'tool', label: action.label || tool.label, hint: tool.hint, icon: tool.icon, run: action.run,
    })),
    ...findHits.map((h) => ({
      kind: h.kind || 'hit',
      label: h.label,
      hint: h.sublabel,
      icon: KIND_ICON[h.kind] || ArrowRight,
      run: () => { if (h.route) navigate(h.route); close(); },
    })),
  ], [matchedTools, findHits, navigate, close]);

  // Reset active index when items change.
  useEffect(() => { setActiveIdx(0); }, [items.length]);

  const onInputKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(items.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const it = items[activeIdx];
      if (it) { it.run(); close(); }
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[100] bg-slate-900/30 backdrop-blur-[2px] flex items-start justify-center pt-[15vh]"
          onClick={close}
          data-testid="cmdk-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
            className="w-[600px] max-w-[92vw] bg-white rounded-2xl shadow-[0_28px_64px_-16px_rgba(15,23,42,0.4)] ring-1 ring-slate-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="cmdk-panel"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
              <Command className="w-4 h-4 text-slate-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onInputKey}
                placeholder="What do you want to do?"
                className="flex-1 bg-transparent text-[15px] text-slate-900 placeholder:text-slate-400 outline-none"
                data-testid="cmdk-input"
              />
              <kbd className="text-[10px] text-slate-400 tabular-nums">esc</kbd>
            </div>

            <div className="max-h-[40vh] overflow-y-auto">
              {items.length === 0 && query && (
                <div className="px-5 py-8 text-center text-[13px] text-slate-400" data-testid="cmdk-empty">
                  No matches. Try <span className="font-mono text-slate-600">find &lt;query&gt;</span> or <span className="font-mono text-slate-600">add demand 5000 of berry on jun 7</span>.
                </div>
              )}
              {items.length === 0 && !query && (
                <Suggestions tools={tools} />
              )}
              {items.map((it, idx) => {
                const Icon = it.icon || ArrowRight;
                const active = idx === activeIdx;
                const showKindChip = it.kind && it.kind !== 'tool' && it.kind !== 'hit';
                return (
                  <button
                    key={`${it.kind}-${idx}`}
                    onClick={() => { it.run(); close(); }}
                    onMouseEnter={() => setActiveIdx(idx)}
                    className={`w-full flex items-center gap-3 px-5 py-3 text-left text-[13px] transition-colors ${
                      active ? 'bg-slate-50 text-slate-900' : 'text-slate-700 hover:bg-slate-50/60'
                    }`}
                    data-testid={`cmdk-item-${idx}`}
                  >
                    <Icon className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-slate-700' : 'text-slate-400'}`} />
                    {showKindChip && (
                      <span className="text-[9px] tracking-[0.12em] uppercase font-semibold text-slate-400 bg-slate-100 rounded px-1.5 py-0.5">
                        {it.kind}
                      </span>
                    )}
                    <span className="flex-1 truncate font-medium">{it.label}</span>
                    {it.hint && <span className="text-[11px] text-slate-400 font-mono truncate max-w-[40%]">{it.hint}</span>}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Suggestions({ tools }) {
  return (
    <div className="px-5 py-4" data-testid="cmdk-suggestions">
      <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-400 mb-2.5">Try</div>
      <div className="space-y-1.5">
        {tools.slice(0, 6).map((t) => (
          <div key={t.id} className="flex items-center justify-between text-[12px]">
            <span className="text-slate-600">{t.label}</span>
            <span className="font-mono text-slate-400">{t.hint}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
